import request from "../util/SuperagentUtils";

export interface BuyerBookingCountsVO {
  pendingCount: number;
  failedCount: number;
}

function isNumeric(value: unknown): boolean {
  return typeof value === 'number'
    ? isFinite(value)
    : typeof value === 'string' && !isNaN(parseFloat(value)) && isFinite(parseFloat(value));
}

/**
 * Coerces a variety of "count" response shapes into a number. Supports:
 * - a bare JSON number (`5`)
 * - a numeric string (`"5"`)
 * - a count value object (`{ count: 5 }`)
 * Returns `null` when the value cannot be interpreted as a count.
 */
function coerceCount(value: unknown): number | null {
  if (isNumeric(value)) {
    return parseFloat(value as string);
  }
  if (value && typeof value === 'object' && 'count' in (value as Record<string, unknown>)) {
    return coerceCount((value as Record<string, unknown>).count);
  }
  return null;
}

const getNumericRequest = async (endpoint: string): Promise<number> => {
  const resp = await request.get(endpoint);
  // Fall back to `resp.text` for endpoints that return the count as text/plain
  // (superagent only populates `resp.body` for parsed content types).
  return coerceCount(resp.body) ?? coerceCount(resp.text) ?? 0;
};

const getRequestCountVo = async (endpoint: string): Promise<number> => {
  const resp = await request.get(endpoint);
  return resp?.body?.count || 0;
};

const getBuyerBookingCounts = async (endpoint: string): Promise<BuyerBookingCountsVO> => {
  const resp = await request.get(endpoint);
  return {
    pendingCount: resp?.body?.pendingCount ?? 0,
    failedCount: resp?.body?.failedCount ?? 0,
  };
};

export { coerceCount, getNumericRequest, getRequestCountVo, getBuyerBookingCounts };
